import { useCallback, useEffect, useRef } from 'react';
import { useLatest } from 'react-use';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  SelectedNode,
  useOptionalDispositiveDiagramSyncContext,
} from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { AfterDeath } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

interface DiagramTileClickSubmissionResult {
  success: boolean;
  submit?: () => Promise<void>;
}

interface UseDiagramNodeSelectionHandlerParams {
  /**
   * The target whose form is currently shown in the right pane (the DP/BDG
   * context's entity id). Clicking this tile is a no-op; clicking any other
   * navigable tile saves the current form, then commits the new target.
   */
  currentTargetId: string;
  isTwoClientHousehold: boolean;
  /** Current form's submitting flag; gates out re-fires while a save is in flight. */
  isSubmitting: boolean;
  /** Validates and persists the current form without closing or switching sections. */
  handleDiagramTileClickSubmission: () => Promise<DiagramTileClickSubmissionResult>;
  /**
   * Optional in-place target switch (DP form's lazy reload). The BDG form omits
   * it — the committed-target body swap loads the new target on its own.
   */
  onSwitchDispositionTargetAfterSubmit?: (selectedTargetId: string) => void;
}

/**
 * Drives "click a diagram tile to edit that target" for both the DP and BDG
 * split-screen forms. When the selected node changes to a different navigable
 * target, it saves the current form first, then commits the new target — so
 * the right-pane swap can't unmount the form before its edits persist.
 */
export function useDiagramNodeSelectionHandler({
  currentTargetId,
  isTwoClientHousehold,
  isSubmitting,
  handleDiagramTileClickSubmission,
  onSwitchDispositionTargetAfterSubmit,
}: UseDiagramNodeSelectionHandlerParams) {
  const syncContext = useOptionalDispositiveDiagramSyncContext();
  const { showFeedback } = useFeedback();

  const selectedNode = syncContext?.selectedNode ?? null;
  const setSelectedNode = syncContext?.setSelectedNode;
  const setCommittedTargetNode = syncContext?.setCommittedTargetNode;

  // The target whose form is actually shown. A failed save reverts the diagram
  // highlight to it so the diagram and form don't drift apart.
  const committedTargetNodeRef = useLatest(
    syncContext?.committedTargetNode ?? null
  );

  const handleDiagramTileClickSubmissionRef = useLatest(
    handleDiagramTileClickSubmission
  );
  const onSwitchDispositionTargetAfterSubmitRef = useLatest(
    onSwitchDispositionTargetAfterSubmit
  );

  // React Hook Form can briefly report isSubmitting=false between the
  // validation and save submissions. Keep a synchronous guard for the full
  // tile-switch operation so a rapid second click cannot start another save.
  const activeTargetNodeRef = useRef<SelectedNode | null>(null);

  const submitAndCommit = useCallback(
    async (node: SelectedNode) => {
      if (!setCommittedTargetNode) return;
      if (activeTargetNodeRef.current) {
        setSelectedNode?.(activeTargetNodeRef.current);
        return;
      }

      activeTargetNodeRef.current = node;
      diagnostics.debug(
        '[useDiagramNodeSelectionHandler] submitting and switching to new node',
        { selectedNodePulid: node.pulid }
      );
      try {
        const result = await handleDiagramTileClickSubmissionRef.current();
        if (result.success) {
          // `submit` is optional: a successful validation with nothing to
          // persist still navigates to the new target.
          await result.submit?.();
          // Commit the target only after the save resolves, so the right-pane
          // swap can't unmount the form before it persists its edits.
          setCommittedTargetNode(node);
          onSwitchDispositionTargetAfterSubmitRef.current?.(node.pulid);
        } else {
          // Revert the highlight to the shown target so it doesn't strand on
          // the clicked tile (and the root overlay keeps syncing).
          setSelectedNode?.(committedTargetNodeRef.current);
          showFeedback(
            'Please fix the form errors before switching to a new node.'
          );
        }
      } catch (error) {
        diagnostics.error(
          '[useDiagramNodeSelectionHandler] tile-click save failed',
          error instanceof Error ? error : null
        );
        setSelectedNode?.(committedTargetNodeRef.current);
        showFeedback('Failed to save. Please try again.');
      } finally {
        activeTargetNodeRef.current = null;
      }
    },
    [
      committedTargetNodeRef,
      handleDiagramTileClickSubmissionRef,
      onSwitchDispositionTargetAfterSubmitRef,
      setCommittedTargetNode,
      setSelectedNode,
      showFeedback,
    ]
  );

  // The last selection we attempted to submit, tracked by object reference.
  const lastAttemptedNodeRef = useRef<SelectedNode | null>(null);

  useEffect(() => {
    if (!selectedNode?.pulid) return;
    if (!setCommittedTargetNode) return;
    // Already showing this target — nothing to switch to.
    if (selectedNode.pulid === currentTargetId) return;
    // Don't re-fire for a selection we've already attempted (same object). A
    // failed save toggles isSubmitting false again, which would otherwise
    // re-run this effect and retry forever. A genuine re-click produces a new
    // SelectedNode object, so retrying after a failure still works.
    if (selectedNode === lastAttemptedNodeRef.current) return;
    // A save is in flight — ignore re-clicks (e.g. double-clicking a tile).
    if (isSubmitting) return;
    // Section placeholders / second-death tiles are not navigable targets.
    if (
      selectedNode.afterDeath === AfterDeath.Second ||
      (!isTwoClientHousehold && selectedNode.afterDeath === AfterDeath.First)
    ) {
      return;
    }

    lastAttemptedNodeRef.current = selectedNode;
    void submitAndCommit(selectedNode);
  }, [
    selectedNode,
    currentTargetId,
    isSubmitting,
    isTwoClientHousehold,
    setCommittedTargetNode,
    submitAndCommit,
  ]);
}
